import React from 'react';
import { Flex, Text, Card, Img, View } from '@smarterservices/smarter-ui';
import CONSTANTS from '../../../config/constants';
import { getStore } from '../../../stores/StoreContext';
import DisplayTime from '../../Common/DisplayTime';
import IdentificationImage from './IdentificationImage';

/**
 * PastImages Component
 * 
 * Displays a list of past identity validation approvals from previous exam sessions.
 * Shows historical headshot and identification images along with validation metadata.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Array<Object>} props.others - Array of past identity validation records
 * @param {string} props.others[].sid - Session ID of the past validation
 * @param {string} props.others[].date - ISO date string of when the validation occurred
 * @param {string} props.others[].headshot - URL to the headshot image
 * @param {string} props.others[].identification - URL to the identification image
 * @param {string} [props.others[].validatedBy] - Name of the person who validated the identity
 * 
 * @returns {JSX.Element|null} Rendered past identity validations or null if no data
 * 
 * @example
 * const others = [
 *   {
 *     sid: "ES123abc",
 *     date: "2025-12-17T09:20:00.000Z",
 *     headshot: "https://example.com/headshot.jpg",
 *     identification: "https://example.com/id.jpg",
 *     validatedBy: "John Doe"
 *   }
 * ];
 * 
 * <PastImages others={others} />
 * 
 * @description
 * Features:
 * - Displays "Past Identity Approvals" heading
 * - Shows validation date in user's timezone using DisplayTime component
 * - Displays validator name if available
 * - Two-column layout with Headshot and Identification cards
 * - Uses IdentificationImage component for consistent image styling
 * - Returns null if no past validations exist
 * 
 * Layout:
 * - Header bar with date and validator information
 * - Two cards side-by-side (50% width each)
 * - Consistent styling with CONSTANTS for padding and spacing
 */
const PastImages = ({ others }) => {
    const ExamSessionStore = getStore('examSessionStore');

    // Return null if no past validations exist
    if (!others || others.length === 0) {
        return null;
    }

    return (
        <>
            <View as="div" margin="medium none none none">
                <Text as="h3" size="large" weight="bold">
                    Past Identity Approvals
                </Text>
            </View>
            {others.map((other, index) => (
                <View key={other.sid || index} as="div" margin="small none">
                    <View
                        as="div"
                        background="secondary"
                        borderColor="primary"
                        borderRadius="medium"
                        borderWidth="small"
                        padding="x-small"
                        margin="none none small none"
                    >
                        <Flex justifyItems="space-between" alignItems="center">
                            <Flex.Item>
                                <Text size="small" weight="bold">
                                    <DisplayTime
                                        date={
                                            other.date
                                        }
                                        tz={ExamSessionStore.userData?.timezone?.name}
                                    />
                                </Text>
                            </Flex.Item>

                            {other.validatedBy && (
                                <Flex.Item>
                                    <Text size="small">
                                        {/*
                                        TODO: Commenting out we can add security around this...
                                        Validated by: {other.validatedBy}*/}
                                    </Text>
                                </Flex.Item>
                            )}

                        </Flex>
                    </View>
                    <Flex>
                        <Flex.Item
                            as="div"
                            width="50%"
                            align="start"
                            padding="none small none none"
                        >
                            <Card>
                                <Card.Header
                                    background="brand"
                                    padding={CONSTANTS.STYLES.CARD_HEADER_PADDING}
                                >
                                    <Card.Title>
                                        <Text variant={CONSTANTS.STYLES.CARD_TITLE_VARIANT}>
                                            Headshot
                                        </Text>
                                    </Card.Title>
                                </Card.Header>
                                <Card.Body padding={CONSTANTS.STYLES.CARD_BODY_PADDING} minHeight="200px">
                                    <IdentificationImage imageUrl={other.headshot} />
                                </Card.Body>
                            </Card>
                        </Flex.Item>
                        <Flex.Item as="div" shouldGrow width="50%" align="start">
                            <Card>
                                <Card.Header
                                    background="brand"
                                    padding={CONSTANTS.STYLES.CARD_HEADER_PADDING}
                                >
                                    <Card.Title>
                                        <Text variant={CONSTANTS.STYLES.CARD_TITLE_VARIANT}>
                                            Identification
                                        </Text>
                                    </Card.Title>
                                </Card.Header>
                                <Card.Body padding={CONSTANTS.STYLES.CARD_BODY_PADDING} minHeight="200px">
                                    <IdentificationImage imageUrl={other.identification} />
                                </Card.Body>
                            </Card>
                        </Flex.Item>
                    </Flex>
                </View>
            ))}
        </>
    );
};

export default PastImages;
